import { useEffect, useRef } from 'react';
import { LOG_API_URL } from '../../../config';
import { getPostHog } from '../../../utils/posthog';
import { SessionManager } from '../../../utils/SessionManager';
import { VideoJSPlayer } from '../types';

type FlushReason = 'ended' | 'unmount';
type QualityDirection = 'up' | 'down' | 'same' | 'unknown';

interface BufferingEvent {
  startedAt: string;
  resumedAt: string;
  durationMs: number;
  playbackTimeSeconds: number;
}

interface SeekEvent {
  startedAt: string;
  completedAt: string;
  durationMs: number;
  fromSeconds: number;
  toSeconds: number;
}

interface QualitySnapshot {
  id: string;
  label: string;
  height: number | null;
  bitrate: number | null;
}

interface QualityChangeEvent {
  at: string;
  from: string | null;
  to: string;
  direction: QualityDirection;
}

interface ErrorEvent {
  at: string;
  code: number | null;
  message: string | null;
}

interface MetricsState {
  sessionStartedAt: string;
  sessionStartedAtEpochMs: number;
  loadStartAtMs: number | null;
  firstFrameAt: string | null;
  ttffMs: number | null;
  playbackStartedAtMs: number | null;
  totalPlaybackMs: number;
  bufferingStartedAtMs: number | null;
  bufferingStartedAt: string | null;
  bufferingEvents: BufferingEvent[];
  totalBufferingMs: number;
  seekingStartedAtMs: number | null;
  seekingStartedAt: string | null;
  seekingFromSeconds: number | null;
  seekEvents: SeekEvent[];
  qualityChanges: QualityChangeEvent[];
  qualitySwitchCount: number;
  qualityUpSwitchCount: number;
  qualityDownSwitchCount: number;
  currentQuality: QualitySnapshot | null;
  errorEvents: ErrorEvent[];
  errorsByCode: Record<string, number>;
  hasMeaningfulMetric: boolean;
  hasEnded: boolean;
  hasFlushed: boolean;
}

interface QualityLevelItem {
  id?: string | number;
  label?: string;
  height?: number;
  bitrate?: number;
  bandwidth?: number;
}

interface QualityLevelListLike {
  selectedIndex?: number;
  selectedIndex_?: number;
  length?: number;
  on?: (event: string, handler: (...args: unknown[]) => void) => void;
  off?: (event: string, handler: (...args: unknown[]) => void) => void;
  [index: number]: QualityLevelItem | undefined;
}

interface MediaErrorLike {
  code?: number;
  message?: string;
}

const getNow = (): number => {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }

  return Date.now();
};

const roundMetric = (value: number): number => Number(value.toFixed(2));

const MAX_EVENTS = 100;

const createMetricsState = (): MetricsState => ({
  sessionStartedAt: new Date().toISOString(),
  sessionStartedAtEpochMs: Date.now(),
  loadStartAtMs: null,
  firstFrameAt: null,
  ttffMs: null,
  playbackStartedAtMs: null,
  totalPlaybackMs: 0,
  bufferingStartedAtMs: null,
  bufferingStartedAt: null,
  bufferingEvents: [],
  totalBufferingMs: 0,
  seekingStartedAtMs: null,
  seekingStartedAt: null,
  seekingFromSeconds: null,
  seekEvents: [],
  qualityChanges: [],
  qualitySwitchCount: 0,
  qualityUpSwitchCount: 0,
  qualityDownSwitchCount: 0,
  currentQuality: null,
  errorEvents: [],
  errorsByCode: {},
  hasMeaningfulMetric: false,
  hasEnded: false,
  hasFlushed: false,
});

const getActiveUserId = (): number | string | null => {
  try {
    return SessionManager.getActiveSession()?.userId ?? null;
  } catch (error) {
    return null;
  }
};

const getQualitySnapshot = (qualityLevels: QualityLevelListLike | null | undefined): QualitySnapshot | null => {
  if (!qualityLevels) {
    return null;
  }

  const selectedIndex =
    typeof qualityLevels.selectedIndex === 'number'
      ? qualityLevels.selectedIndex
      : typeof qualityLevels.selectedIndex_ === 'number'
        ? qualityLevels.selectedIndex_
        : -1;

  if (selectedIndex < 0) {
    return null;
  }

  const selectedLevel = qualityLevels[selectedIndex];
  if (!selectedLevel) {
    return null;
  }

  const height = typeof selectedLevel.height === 'number' ? selectedLevel.height : null;
  const bitrate =
    typeof selectedLevel.bitrate === 'number'
      ? selectedLevel.bitrate
      : typeof selectedLevel.bandwidth === 'number'
        ? selectedLevel.bandwidth
        : null;

  return {
    id: String(selectedLevel.id ?? selectedLevel.label ?? selectedLevel.height ?? selectedIndex),
    label: height ? `${height}p` : String(selectedLevel.label ?? selectedLevel.id ?? 'unknown'),
    height,
    bitrate,
  };
};

const getQualityDirection = (
  previousQuality: QualitySnapshot | null,
  nextQuality: QualitySnapshot
): QualityDirection => {
  if (!previousQuality) {
    return 'unknown';
  }

  if (
    typeof previousQuality.height === 'number' &&
    typeof nextQuality.height === 'number'
  ) {
    if (nextQuality.height > previousQuality.height) {
      return 'up';
    }
    if (nextQuality.height < previousQuality.height) {
      return 'down';
    }
    return 'same';
  }

  if (previousQuality.id === nextQuality.id) {
    return 'same';
  }

  return 'unknown';
};

const sendStructuredLog = (payload: Record<string, unknown>, flushReason: FlushReason): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const body = JSON.stringify({
    timestamp: new Date().toISOString(),
    level: 'INFO',
    message: 'Video playback metrics collected',
    type: 'video_playback_metrics',
    userId: getActiveUserId(),
    userAgent: window.navigator.userAgent,
    url: window.location.href,
    data: {
      service: 'frontend',
      flush_reason: flushReason,
      ...payload,
    },
  });

  fetch(LOG_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body,
    keepalive: true,
  }).catch(() => {
    // メトリクス送信失敗は再生体験に影響させない
  });
};

export const useVideoMetrics = (player: VideoJSPlayer | null, videoId: string): void => {
  const stateRef = useRef<MetricsState>(createMetricsState());

  useEffect(() => {
    if (!player || !videoId) {
      return;
    }

    const state = createMetricsState();
    stateRef.current = state;

    const qualityLevels = (player as any).qualityLevels?.() as QualityLevelListLike | undefined;

    const safeCurrentTime = (): number | null => {
      try {
        return player.currentTime() ?? null;
      } catch {
        return null;
      }
    };

    const stopPlaybackTimer = (): void => {
      if (state.playbackStartedAtMs === null) {
        return;
      }

      state.totalPlaybackMs += getNow() - state.playbackStartedAtMs;
      state.playbackStartedAtMs = null;
    };

    const startPlaybackTimer = (): void => {
      if (state.playbackStartedAtMs !== null) {
        return;
      }

      state.playbackStartedAtMs = getNow();
    };

    const finishBuffering = (): void => {
      if (state.bufferingStartedAtMs === null || !state.bufferingStartedAt) {
        return;
      }

      const durationMs = getNow() - state.bufferingStartedAtMs;

      if (state.bufferingEvents.length < MAX_EVENTS) {
        state.bufferingEvents.push({
          startedAt: state.bufferingStartedAt,
          resumedAt: new Date().toISOString(),
          durationMs: roundMetric(durationMs),
          playbackTimeSeconds: safeCurrentTime() ?? 0,
        });
      }
      state.totalBufferingMs += durationMs;
      state.bufferingStartedAtMs = null;
      state.bufferingStartedAt = null;
      state.hasMeaningfulMetric = true;
    };

    const flushMetrics = (reason: FlushReason): void => {
      if (state.hasFlushed) {
        return;
      }

      stopPlaybackTimer();
      finishBuffering();

      if (state.seekingStartedAtMs !== null && state.seekingStartedAt) {
        const seekToSeconds = safeCurrentTime() ?? state.seekingFromSeconds ?? 0;

        if (state.seekEvents.length < MAX_EVENTS) {
          state.seekEvents.push({
            startedAt: state.seekingStartedAt,
            completedAt: new Date().toISOString(),
            durationMs: roundMetric(getNow() - state.seekingStartedAtMs),
            fromSeconds: state.seekingFromSeconds ?? 0,
            toSeconds: seekToSeconds,
          });
        }
        state.seekingStartedAtMs = null;
        state.seekingStartedAt = null;
        state.seekingFromSeconds = null;
      }

      if (
        !state.hasMeaningfulMetric &&
        state.ttffMs === null &&
        state.errorEvents.length === 0 &&
        state.qualitySwitchCount === 0
      ) {
        state.hasFlushed = true;
        return;
      }

      const totalPlaybackMs = roundMetric(state.totalPlaybackMs);
      const totalBufferingMs = roundMetric(state.totalBufferingMs);
      const averageSeekResponseMs =
        state.seekEvents.length > 0
          ? roundMetric(
              state.seekEvents.reduce((sum, event) => sum + event.durationMs, 0) /
                state.seekEvents.length
            )
          : 0;
      const maxSeekResponseMs =
        state.seekEvents.length > 0
          ? Math.max(...state.seekEvents.map((event) => event.durationMs))
          : 0;

      const payload = {
        video_id: videoId,
        session_started_at: state.sessionStartedAt,
        session_ended_at: new Date().toISOString(),
        session_duration_ms: roundMetric(Date.now() - state.sessionStartedAtEpochMs),
        ttff_ms: state.ttffMs,
        first_frame_at: state.firstFrameAt,
        playback_time_ms: totalPlaybackMs,
        buffering_count: state.bufferingEvents.length,
        buffering_time_ms: totalBufferingMs,
        buffering_ratio: totalPlaybackMs > 0 ? roundMetric(totalBufferingMs / totalPlaybackMs) : 0,
        buffering_events: state.bufferingEvents,
        seek_count: state.seekEvents.length,
        seek_response_avg_ms: averageSeekResponseMs,
        seek_response_max_ms: maxSeekResponseMs,
        seek_events: state.seekEvents,
        quality_switch_count: state.qualitySwitchCount,
        quality_up_switch_count: state.qualityUpSwitchCount,
        quality_down_switch_count: state.qualityDownSwitchCount,
        current_quality: state.currentQuality?.label ?? null,
        quality_events: state.qualityChanges,
        errors_by_code: state.errorsByCode,
        error_events: state.errorEvents,
        ended: state.hasEnded,
        flush_reason: reason,
      };

      const posthog = getPostHog();
      if (posthog) {
        try {
          posthog.capture('video_playback_metrics', payload);
        } catch {
          // PostHog SDK の例外は再生体験に影響させない
        }
      }

      sendStructuredLog(payload, reason);
      state.hasFlushed = true;
    };

    const updateCurrentQuality = (): void => {
      const nextQuality = getQualitySnapshot(qualityLevels);

      if (!nextQuality) {
        return;
      }

      if (!state.currentQuality) {
        state.currentQuality = nextQuality;
        return;
      }

      if (state.currentQuality.id === nextQuality.id) {
        state.currentQuality = nextQuality;
        return;
      }

      const direction = getQualityDirection(state.currentQuality, nextQuality);
      if (state.qualityChanges.length < MAX_EVENTS) {
        state.qualityChanges.push({
          at: new Date().toISOString(),
          from: state.currentQuality.label,
          to: nextQuality.label,
          direction,
        });
      }
      state.qualitySwitchCount += 1;
      if (direction === 'up') {
        state.qualityUpSwitchCount += 1;
      }
      if (direction === 'down') {
        state.qualityDownSwitchCount += 1;
      }
      state.currentQuality = nextQuality;
      state.hasMeaningfulMetric = true;
    };

    const handleLoadStart = (): void => {
      // 再読み込み開始はバッファリング区切りとして扱う
      // waiting → loadstart → waiting のサイクルで2回目以降の waiting が
      // ガードにより無視されるのを防ぐため、ここで前のバッファリングを閉じる
      finishBuffering();
      state.loadStartAtMs = getNow();
      state.hasMeaningfulMetric = true;
      updateCurrentQuality();
    };

    const handlePlaying = (): void => {
      if (state.ttffMs === null && state.loadStartAtMs !== null) {
        state.ttffMs = roundMetric(getNow() - state.loadStartAtMs);
        state.firstFrameAt = new Date().toISOString();
      }

      finishBuffering();
      startPlaybackTimer();
      updateCurrentQuality();
    };

    const handleWaiting = (): void => {
      if (state.seekingStartedAtMs !== null || state.bufferingStartedAtMs !== null) {
        return;
      }

      stopPlaybackTimer();
      state.bufferingStartedAtMs = getNow();
      state.bufferingStartedAt = new Date().toISOString();
      state.hasMeaningfulMetric = true;
    };

    const handlePause = (): void => {
      stopPlaybackTimer();
    };

    const handleSeeking = (): void => {
      stopPlaybackTimer();

      if (state.seekingStartedAtMs !== null) {
        return;
      }

      state.seekingStartedAtMs = getNow();
      state.seekingStartedAt = new Date().toISOString();
      state.seekingFromSeconds = safeCurrentTime() ?? 0;
      state.hasMeaningfulMetric = true;
    };

    const handleSeeked = (): void => {
      if (state.seekingStartedAtMs === null || !state.seekingStartedAt) {
        return;
      }

      if (state.seekEvents.length < MAX_EVENTS) {
        state.seekEvents.push({
          startedAt: state.seekingStartedAt,
          completedAt: new Date().toISOString(),
          durationMs: roundMetric(getNow() - state.seekingStartedAtMs),
          fromSeconds: state.seekingFromSeconds ?? 0,
          toSeconds: safeCurrentTime() ?? 0,
        });
      }
      state.seekingStartedAtMs = null;
      state.seekingStartedAt = null;
      state.seekingFromSeconds = null;

      // シーク完了後のセグメント読み込み待ちをバッファリングとして計測開始
      // seeking 中は waiting がガードでスキップされるため、seeked 時点から計測して
      // playing で finishBuffering() により終了させる
      if (state.bufferingStartedAtMs === null) {
        stopPlaybackTimer();
        state.bufferingStartedAtMs = getNow();
        state.bufferingStartedAt = new Date().toISOString();
        state.hasMeaningfulMetric = true;
      }
    };

    const handleError = (): void => {
      const error = player.error() as MediaErrorLike | null;
      const codeKey = String(error?.code ?? 'unknown');

      if (state.errorEvents.length < MAX_EVENTS) {
        state.errorEvents.push({
          at: new Date().toISOString(),
          code: error?.code ?? null,
          message: error?.message ?? null,
        });
      }
      state.errorsByCode[codeKey] = (state.errorsByCode[codeKey] ?? 0) + 1;
      state.hasMeaningfulMetric = true;
    };

    const handleEnded = (): void => {
      state.hasEnded = true;
      flushMetrics('ended');
    };

    player.on('loadstart', handleLoadStart);
    player.on('playing', handlePlaying);
    player.on('waiting', handleWaiting);
    player.on('stalled', handleWaiting);
    player.on('pause', handlePause);
    player.on('seeking', handleSeeking);
    player.on('seeked', handleSeeked);
    player.on('error', handleError);
    player.on('ended', handleEnded);
    qualityLevels?.on?.('change', updateCurrentQuality);

    updateCurrentQuality();

    return () => {
      try {
        player.off('loadstart', handleLoadStart);
        player.off('playing', handlePlaying);
        player.off('waiting', handleWaiting);
        player.off('stalled', handleWaiting);
        player.off('pause', handlePause);
        player.off('seeking', handleSeeking);
        player.off('seeked', handleSeeked);
        player.off('error', handleError);
        player.off('ended', handleEnded);
        qualityLevels?.off?.('change', updateCurrentQuality);
      } catch {
        // dispose 済みプレイヤーでの off() 呼び出しエラーを無視
      }
      flushMetrics('unmount');
    };
  }, [player, videoId]);
};
